import React, { useEffect, useState } from 'react'
import { RiEditLine, RiDeleteBinLine } from 'react-icons/ri'
import axios from 'axios';
import Cookies from 'js-cookie';
import API_BASE_URL from '../../../config/api';
import Swal from 'sweetalert2';
import Topbar from '../mini-comp/Topbar';
import Sidebar from '../mini-comp/Sidebar';

const Departments = () => {
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Assign Manager States
    const [managers, setManagers] = useState([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
    const [selectedManagerId, setSelectedManagerId] = useState('');
    const [assigning, setAssigning] = useState(false);

    const fetchDepartments = async () => {
        try {
            const token = Cookies.get('token');
            if (!token) {
                console.error('No token found');
                return;
            }
            const response = await axios.get(`${API_BASE_URL}/api/v1/employees/department/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setDepartments(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching departments:', error);
            setLoading(false);
        }
    };

    const fetchManagers = async () => {
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/v1/employees/immediate-managers/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            setManagers(response.data.immediate_managers);
        } catch (error) {
            console.error('Error fetching managers:', error);
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchManagers();
    }, []);

    const handleCancelEdit = () => {
        setNewDepartmentName('');
        setSelectedManagerId('');
        setSelectedDepartmentId(null);
        setIsEditing(false);
    };

    const handleCreateDepartment = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const token = Cookies.get('token');

        try {
            await axios.post(`${API_BASE_URL}/api/v1/employees/department/`,
                { name: newDepartmentName },
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Department created successfully!',
                timer: 1500,
                showConfirmButton: false
            });

            setNewDepartmentName('');
            // Close modal using bootstrap button
            const closeBtn = document.querySelector('#addNewDepartment .btn-close');
            if (closeBtn) closeBtn.click();

            fetchDepartments();
        } catch (error) {
            console.error('Error creating department:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to create department. Please try again.'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateDepartment = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const token = Cookies.get('token');

        try {
            const payload = {
                name: newDepartmentName,
                manager_id: selectedManagerId || null
            };

            await axios.put(`${API_BASE_URL}/api/v1/employees/department/${selectedDepartmentId}/`,
                payload,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Department updated successfully!',
                timer: 1500,
                showConfirmButton: false
            });

            handleCancelEdit();

            // Close modal using bootstrap button
            const closeBtn = document.querySelector('#editDepartmentModal .btn-close');
            if (closeBtn) closeBtn.click();

            fetchDepartments();
        } catch (error) {
            console.error('Error updating department:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update department. Please try again.'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const openEditModal = (dept) => {
        setNewDepartmentName(dept.name);
        setSelectedManagerId(dept.manager ? dept.manager.id : '');
        setSelectedDepartmentId(dept.id);
        setIsEditing(true);
    };

    const handleAssignManager = async (e) => {
        e.preventDefault();
        setAssigning(true);
        const token = Cookies.get('token');

        try {
            await axios.patch(`${API_BASE_URL}/api/v1/employees/department/${selectedDepartmentId}/`,
                { manager_id: selectedManagerId },
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Manager assigned successfully!',
                timer: 1500,
                showConfirmButton: false
            });

            setSelectedManagerId('');
            setSelectedDepartmentId(null);

            // Close modal
            const closeBtn = document.querySelector('#assignManagerModal .btn-close');
            if (closeBtn) closeBtn.click();

            fetchDepartments();
        } catch (error) {
            console.error('Error assigning manager:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to assign manager. Please try again.'
            });
        } finally {
            setAssigning(false);
        }
    };

    const handleDeleteDepartment = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token');
                await axios.delete(`${API_BASE_URL}/api/v1/employees/department/${id}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                Swal.fire(
                    'Deleted!',
                    'Department has been deleted.',
                    'success'
                );
                fetchDepartments();
            } catch (error) {
                console.error('Error deleting department:', error);
                Swal.fire(
                    'Error!',
                    'Failed to delete department.',
                    'error'
                );
            }
        }
    };

    return (
        <div>
            <Topbar />
            <Sidebar />

            <>{ /* app-content-area-start */}
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid">
                            <div className="row fix">
                                <div className="col-xl-12">
                                    <div className="card">
                                        <div className="card-header justify-between">
                                            <h4 className="d-flex-items gap-10">Departments</h4>
                                            <div className="d-flex flex-wrap gap-15">
                                                <button type="button" className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#addNewDepartment" onClick={() => setIsEditing(false)}>
                                                    Add Department
                                                </button>

                                            </div>
                                        </div>
                                        <div className="card-body pt-15">
                                            <div className="col-xxl-12 col-xl-12 col-lg-12">
                                                <div className="row">
                                                    {loading ? (
                                                        <div className="col-12 text-center py-4">
                                                            <div className="spinner-border text-primary" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </div>
                                                    ) : departments.length > 0 ? (
                                                        departments.map((dept) => (
                                                            <div key={dept.id} className="col-xxl-3 col-xl-4 col-lg-4 col-md-4 mb-4">
                                                                <div className="product-card">
                                                                    <div className="product-content">
                                                                        <h5 className="mb-5 underline">
                                                                            {dept.name}
                                                                        </h5>
                                                                        <hr />
                                                                        <p className="fs-16">Manager: <span className="fs-16">{dept.manager?.first_name || "N/A"} {dept.manager?.last_name || "N/A"}</span></p>
                                                                        <p><div className="fs-12 mb-0">{dept.manager?.email || 'N/A'}</div></p>
                                                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                                                            <div>
                                                                                <button
                                                                                    className="btn btn-sm btn-primary-light me-2"
                                                                                    title="Edit"
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#editDepartmentModal"
                                                                                    onClick={() => openEditModal(dept)}
                                                                                >
                                                                                    <RiEditLine className='fs-12' />
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-danger-light"
                                                                                    title="Delete"
                                                                                    onClick={() => handleDeleteDepartment(dept.id)}
                                                                                >
                                                                                    <RiDeleteBinLine className='fs-12' />
                                                                                </button>
                                                                            </div>
                                                                            {!dept.manager && (
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#assignManagerModal"
                                                                                    onClick={() => setSelectedDepartmentId(dept.id)}
                                                                                >
                                                                                    Assign Manager
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="col-12 text-center">
                                                            <p>No departments found.</p>
                                                        </div>
                                                    )}
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
                { /* app-content-area-end */}</>

            {/* Create Department Modal */}
            <div className="modal fade" id="addNewDepartment" tabIndex="-1" aria-labelledby="addNewDepartmentLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="addNewDepartmentLabel">Add New Department</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleCreateDepartment}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="departmentName" className="form-label">Department Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="departmentName"
                                        value={newDepartmentName}
                                        onChange={(e) => setNewDepartmentName(e.target.value)}
                                        required
                                        placeholder="Enter department name"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" disabled={submitting}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? 'Creating...' : 'Create Department'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Assign Manager Modal */}
            <div className="modal fade" id="assignManagerModal" tabIndex="-1" aria-labelledby="assignManagerModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="assignManagerModalLabel">Assign Manager</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleAssignManager}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="managerSelect" className="form-label">Select Manager <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select"
                                        id="managerSelect"
                                        value={selectedManagerId}
                                        onChange={(e) => setSelectedManagerId(e.target.value)}
                                        required
                                    >
                                        <option value="">Choose a manager...</option>
                                        {Array.isArray(managers) && managers.map(manager => (
                                            <option key={manager.id} value={manager.id}>
                                                {manager.first_name} {manager.last_name} ({manager.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" disabled={assigning}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={assigning}>
                                    {assigning ? 'Assigning...' : 'Assign Manager'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Edit Department Modal */}
            <div className="modal fade" id="editDepartmentModal" tabIndex="-1" aria-labelledby="editDepartmentModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="editDepartmentModalLabel">Edit Department</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleUpdateDepartment}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="editDepartmentName" className="form-label">Department Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="editDepartmentName"
                                        value={newDepartmentName}
                                        onChange={(e) => setNewDepartmentName(e.target.value)}
                                        required
                                        placeholder="Enter department name"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="editManagerSelect" className="form-label">Manager</label>
                                    <select
                                        className="form-select"
                                        id="editManagerSelect"
                                        value={selectedManagerId}
                                        onChange={(e) => setSelectedManagerId(e.target.value)}
                                    >
                                        <option value="">Select Manager (Optional)</option>
                                        {Array.isArray(managers) && managers.map(manager => (
                                            <option key={manager.id} value={manager.id}>
                                                {manager.first_name} {manager.last_name} ({manager.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" disabled={submitting}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? 'Updating...' : 'Update Department'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default Departments
